import React, {useEffect} from "react";
import {
    ActionType,
    Column,
    ColumnType,
    FixedSelectionFilter,
    FreeTextFilter,
    GetParams,
    IMAGES,
    KButton,
    KBUTTON_SIZE,
    KBUTTON_VARIANT,
    KCard,
    KContainer,
    KModal,
    KModalSize,
    KModalVariant,
    KSpace,
    KSpinner,
    KSpinnerSize,
    KTableLoader,
    Table
} from "@kopjra/uikit";
import {Credential, ProductCredits} from "../types/users";
import {I18n, Localize, Translate} from "react-redux-i18n";
import {tableGetParams, tableHasFilter} from "../utils/commons";
import {Alert, Col, Row} from "react-bootstrap";
import {PdfSignature} from "../types/pdfSignatures";
import {AppStatus, AppStatusToColorMap, AppStatusToIconMap, AppStatusToInteractionStatusMap, InteractionStatus, InteractionStatusAppStatusMap} from "../types/signatures";
import ReactJson from "react-json-view";
import {KCredit, KCreditColor} from "@kopjra/uikit/dist/src/lib/KCredit";
import {BuyCTA} from "./helpers/BuyCTA";

export interface StateProps {
    apiKeys?: Credential[];
    signatures?: PdfSignature[];
    total: number;
    stats?: ProductCredits;
    json?: object;
    isAllowed: boolean;
}

export interface DispatchProps {
    onGetPdfSignatures: (q: GetParams) => Promise<void>;
    onGoToApiKeys: () => void;
    onGoToDocumentation: () => void;
    onDownloadPdfXML: (sig: PdfSignature) => Promise<void>;
    onDownloadPdf: (sig: PdfSignature) => Promise<void>;
    onGetPdfJson: (sig: PdfSignature) => Promise<void>;
    onCleanJson: () => void;
    onGetUserCredits: () => Promise<void>;
    onResetPdfSignatures: () => void;
}

export interface InnerProps {
}

export type Props = StateProps & DispatchProps & InnerProps;

const tableName = "pdf_signatures";

export const PdfInteractions: React.FC<Props> = ({onResetPdfSignatures, onGetUserCredits, onDownloadPdf, isAllowed, onGetPdfJson, json, onCleanJson, onDownloadPdfXML, stats, onGetPdfSignatures, signatures, total, onGoToApiKeys, onGoToDocumentation, apiKeys}) => {
    useEffect(() => {
        (async () => {
            await onGetPdfSignatures(tableGetParams(tableName));
        })().catch();
    }, [onGetPdfSignatures, stats]);

    const actions: ActionType<PdfSignature>[] = [
        {
            name: <div className="nowrap"><i className="fal fa-info-circle"/> <Translate value="interaction.table.details"/></div>,
            handler: (datum) => onGetPdfJson(datum),
            shouldRender: (datum) => datum.currentStatus === InteractionStatus.Concluded,
        },
        {
            name: <span className="nowrap"><i className="fal fa-download"/> <Translate value="interaction.table.downloadXML"/></span>,
            handler: (datum) => onDownloadPdfXML(datum),
            shouldRender: (datum) => datum.currentStatus === InteractionStatus.Concluded && !datum.deletedAt,
        },
        {
            name: <span className="nowrap"><i className="fal fa-download"/> <Translate value="interaction.table.downloadPDF"/></span>,
            handler: (datum) => onDownloadPdf(datum),
            shouldRender: (datum) => datum.currentStatus === InteractionStatus.Concluded && !datum.deletedAt,
        },
    ];

    return (
        <KContainer className="text-center">
            {(apiKeys && signatures && stats) ? (signatures.length  || tableHasFilter(tableName)) ? (
                <KCard header={<><Translate value="interaction.pdfTitle"/></>} actions={[
                    {element: <i className="fal fa-redo"/>, action: async () => {onResetPdfSignatures(); await Promise.all([onGetUserCredits(), onGetPdfSignatures(tableGetParams(tableName))]);}}
                ]}>
                    <Row>
                        <Col md={3} className="text-center">
                            <KCredit infinite={stats.availableQty === -1} remaining={stats.availableQty} total={stats.subscribedQty} labelText={<Translate value="interaction.available"/>}/>
                        </Col>
                        <Col md={3} className="text-center">
                            <KCredit infinite={stats.consumedQtyExtra === -1} remaining={stats.consumedQtyExtra} labelText={<Translate value="interaction.overQuota"/>}/>
                        </Col>
                        <Col md={3} className="text-center">
                            <KCredit color={KCreditColor.SUCCESS} infinite={stats.consumedTotal === -1} remaining={stats.consumedTotal} labelText={<Translate value="interaction.totalUsed"/>}/>
                        </Col>
                    </Row>
                    {stats.availableQty < 1 && stats.availableQty !== -1 ? (
                        <>
                            <KSpace spaces={1}/>
                            <Row>
                                <Col md={6}>
                                    <p><Alert variant={"warning"}><Translate value={"interaction.empty"} dangerousHTML={true}/></Alert></p>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    <KSpace spaces={2}/>
                    <Table
                        id={tableName}
                        data={signatures}
                        total_count={total}
                        loaderFunc={async (q: GetParams) => onGetPdfSignatures(q)}
                        keyField={"id"}
                        waiter={<KSpinner size={KSpinnerSize.xl}/>}
                        filterDefinition={[
                            new FreeTextFilter("signerFullname", <Translate value="interaction.table.filter.signerFullname"/>),
                            // new FreeTextFilter("otpSms", <Translate value="interaction.table.filter.otpSms"/>),
                            // new FixedSelectionFilter("signatureType", <Translate value="interaction.table.filter.signatureType"/>,
                            //     Object.values(SignatureTypes).map((sigType) => ({
                            //         label: I18n.t(`interaction.table.signatureTypes.${sigType}`),
                            //         value: sigType,
                            //     })),
                            // ),
                            new FixedSelectionFilter("currentStatus", <Translate value="interaction.table.filter.currentStatus"/>,
                                Object.values(AppStatus).map((appStatus) => ({
                                    label: I18n.t(`interaction.table.appStatus.${appStatus}` as any),
                                    value: AppStatusToInteractionStatusMap[appStatus],
                                })),
                            ),
                            new FreeTextFilter("tagKey", <Translate value="interaction.table.filter.tagKey"/>),
                            new FreeTextFilter("tagValue", <Translate value="interaction.table.filter.tagValue"/>),
                        ]}
                    >
                        <Column colid={"id"} type={ColumnType.GENERIC} name="interaction.table.id" sort="id" colspan={1} classes="text-left"
                                render={(datum: PdfSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.id}</span>}
                        />
                        <Column colid={"createdAt"} type={ColumnType.GENERIC} name="interaction.table.createdAt" sort="createdAt" colspan={2} classes="text-left"
                                render={(datum: PdfSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}><Localize value={datum.createdAt} dateFormat={"kptable.date.long"}/></span>}
                        />
                        <Column colid={"signatureType"} type={ColumnType.GENERIC} name="interaction.table.signatureType" sort="oURL" colspan={2} classes="text-left"
                                render={(datum: PdfSignature) => <Translate className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""} value={`interaction.table.signatureTypes.${datum.signatureType}` as any} />}
                        />
                        <Column colid={"signerFullname"} type={ColumnType.GENERIC} name="interaction.table.signerFullname" sort="signerFullname" colspan={2} classes="text-left"
                                render={(datum: PdfSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.signerFullname}</span>}
                        />
                        <Column colid={"otpSms"} type={ColumnType.GENERIC} name="interaction.table.otpSms" sort="otpSms" colspan={1} classes="text-left" visible={false}
                                render={(datum: PdfSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.otpSms}</span>}
                        />
                        <Column colid={"currentStatus"} type={ColumnType.GENERIC} name="interaction.table.currentStatus" sort="currentStatus" colspan={1} classes="text-left nowrap"
                                render={(datum: PdfSignature) => {
                                    const appStatus = InteractionStatusAppStatusMap[datum.currentStatus];
                                    return <>
                                        <i className={`fal fa-${AppStatusToIconMap[appStatus]} text-${AppStatusToColorMap[appStatus]}`}/>&nbsp;<Translate className={`tableStatus ${datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}`} value={`interaction.table.appStatus.${appStatus}` as any}/>
                                    </>
                                }}
                        />
                        <Column colid={"userLandedAt"} type={ColumnType.GENERIC} name="interaction.table.userLandedAt" sort="userLandedAt" colspan={2} visible={false} classes="text-left"
                                render={(datum: PdfSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.userLandedAt && <Localize value={datum.userLandedAt} dateFormat="kptable.date.long"/>}</span>}
                        />
                        <Column colid={"userTookoffAt"} type={ColumnType.GENERIC} name="interaction.table.userTookoffAt" sort="userTookoffAt" colspan={2} visible={false} classes="text-left"
                                render={(datum: PdfSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.userTookoffAt && <Localize value={datum.userTookoffAt} dateFormat="kptable.date.long"/>}</span>}
                        />
                        <Column colid="actions" type={ColumnType.ACTIONS} actions={actions} colspan={3} classes="text-right"/>
                    </Table>
                </KCard>
            ) : isAllowed ? (
                <>
                    <KSpace spaces={3}/>
                    <img src={IMAGES["web-trust"].still.large.dark} height={165} alt={"Web Trust"}/>
                    <KSpace/>
                    <h4><Translate value="interaction.noInteraction"/></h4>
                    <KSpace spaces={2}/>
                    {apiKeys.length ? (
                        <h4><Translate value="interaction.withKeys"/></h4>
                    ) : (
                        <h4><Translate value="interaction.noKeys"/></h4>
                    )}
                    <KSpace spaces={2}/>
                    <div>
                        {!apiKeys.length ? (
                            <>
                                <KButton
                                    text={<><i className="fal fa-angle-double-right"/> <Translate value="user.apiKeys.title"/></>}
                                    variant={KBUTTON_VARIANT.primary}
                                    size={KBUTTON_SIZE.nm}
                                    onClick={() => onGoToApiKeys()}
                                />
                                <KSpace v={true} spaces={2}/>
                            </>
                        ) : null}
                        <KButton
                            text={<><i className="fal fa-angle-double-right"/> <Translate value="user.documentation"/></>}
                            variant={KBUTTON_VARIANT.primary}
                            size={KBUTTON_SIZE.nm}
                            onClick={() => onGoToDocumentation()}
                        />
                    </div>
                    {/*<KSpace spaces={2}/>*/}
                    {/*<h4><a className="link" href={`mailto:info@kopjra.com?subject=${I18n.t("home.contactSubject")}&body=${I18n.t("home.contactBody")}`} target="_blank" rel="noopener noreferrer"><Translate value="home.contactus"/></a></h4>*/}
                </>
            ) : (
                <BuyCTA/>
            ) : (
                <KCard header={<Translate value="interaction.pdfTitle"/>}>
                    <KTableLoader/>
                </KCard>
            )}
            <KModal show={!!json} header={<Translate value={"interaction.table.details"}/>}
                    onHide={() => onCleanJson()} variant={KModalVariant.secondary} size={KModalSize.lg} buttonText={<Translate value="generic.close"/>}>
                {json ? (
                    <div style={{width: "100%", overflowX: "auto"}}>
                        <ReactJson src={json} collapsed={1} collapseStringsAfterLength={15} displayObjectSize={false}/>
                    </div>
                ) : null}
            </KModal>
        </KContainer>
    );
}
